/* eslint-disable @typescript-eslint/no-floating-promises */
import { Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { usePopup } from "@nodeon/react-components";
import { Map } from "ol";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "~/redux/store/store";
import BerthOrdersList from "../components/ports/BerthOrdersList";
import { BerthMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: BerthMapItem;
  hoverData?: BerthMapItem;
};

type HoverProps = {
  hoverData: BerthMapItem;
  clickData?: BerthMapItem;
};

export const BerthPopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("berths");
  const { show } = usePopup();
  const isMobile = useAppSelector((x) => x.general.isMobile);

  const data = clickData ?? hoverData;

  const onClick = useCallback(() => {
    if (!data) return;
    show(({ close }) => <BerthOrdersList berth={data} onClose={close} />, {
      dialogProps: {
        maxWidth: isMobile ? undefined : "lg",
        fullScreen: isMobile,
        fullWidth: true,
      },
    });
  }, [data, isMobile, show]);

  if (!data) return null;
  const { name, description, power, water, garbage, capacity } = data;

  return (
    <GenericPopup
      map={map}
      clicked={!!clickData}
      avatarText={name?.[0] ?? ""}
      title={`${t("berth")} - ${name ?? ""}`}
      subheader={description ?? ""}
      contentProps={{
        style: { padding: 0, maxHeight: clickData ? 600 : undefined },
      }}
      headerProps={{
        style: {
          paddingBottom: clickData ? 0 : undefined,
        },
      }}
      content={
        <Box p={2}>
          <Typography variant="body2" align="left" noWrap>
            {`${t("power")}: ${t(power ? "general:yes" : "general:no")}`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("water")}: ${t(water ? "general:yes" : "general:no")}`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("garbage")}: ${t(garbage ? "general:yes" : "general:no")}`}
          </Typography>
          <Typography variant="body2" align="left" noWrap>
            {`${t("capacity")}: ${capacity}`}
          </Typography>
          <Button
            variant="outlined"
            onClick={onClick}
            sx={{ position: "absolute", right: 10, bottom: 10 }}
          >
            {t("orders")}
          </Button>
        </Box>
      }
    />
  );
};
